import { React } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useSelector } from 'react-redux';
import MessagePlaceholder from '../Parts/MessagePlaceholder';
import {
  convertISOFormatToDate,
  convertISOFormatToOnlyDate,
} from '../../lib/tools';
import { getStatusText, createReportNo } from '../../lib/reportTools';
import {
  MainBlock,
  ScrolledBlock,
  Table,
  Thead,
  Tbody,
  TrHead,
  TrBody,
  Th,
  Td,
  ThCheckbox,
  TdCheckbox,
  ThSmall,
  TdSmall,
  CheckboxContainer,
} from './styles/ReportListStyles';
import { PAGE_PATH } from '../../lib/constants';
import useCheckboxState from './hooks/useCheckboxState';
import { ReportStatus } from '../ReportEdit/TitleArea';
import accessible from './functions/authControl';

/**
 * 報告書の一覧表を表示するコンポーネント
 *
 * @component
 * @param {Object[]} reports - 表示する報告書の配列。
 * @param {Function} onSelect - 1つのチェックボックス選択時（または選択解除）に呼び出される関数。
 * @param {Function} onSelectAll - チェックボックスを全選択時（または選択解除）に呼び出される関数。
 * @param {number} currentUserRole - ログインユーザーの権限。権限に応じてチェックボックスの表示が変わる
 */
const ReportList = ({
  reports,
  onSelect,
  onSelectAll,
  currentUserRole,
  currentPage,
}) => {
  const targetGroupId = useSelector(state => state.main.targetGroupId);
  const currentUserInfo = useSelector(state => state.main.userInfo);
  const groups = useSelector(state => state.group.groups);

  const { allChecked, checkedItems, handleAllChecked, handleCheckChange } =
    useCheckboxState(
      reports,
      onSelect,
      onSelectAll,
      currentUserRole,
      currentUserInfo,
      currentPage,
    );

  const history = useHistory();
  const openDetail = target => {
    const { id: reportId, status } = target;
    // 完了ステータスの場合はプレビュー画面を表示、それ以外は編集画面を表示する
    const url =
      status === ReportStatus.complete
        ? `/ds/report/${reportId}`
        : `${PAGE_PATH.DS_REPORT_EDIT}/${reportId}`;
    history.push(url);
  };

  return (
    <MainBlock>
      <div>
        <Table>
          <Thead>
            <TrHead>
              <ThCheckbox>
                <CheckboxContainer>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={handleAllChecked}
                  />
                </CheckboxContainer>
              </ThCheckbox>
              <ThSmall>
                <FormattedMessage id="report.list.header.number" />
              </ThSmall>
              <Th>
                <FormattedMessage id="report.list.header.subject" />
              </Th>
              <ThSmall>
                <FormattedMessage id="report.list.header.status" />
              </ThSmall>
              <ThSmall>
                <FormattedMessage id="report.list.header.author" />
              </ThSmall>
              <ThSmall>
                <FormattedMessage id="report.list.header.createDate" />
              </ThSmall>
              <ThSmall>
                <FormattedMessage id="report.list.header.lastModifiedBy" />
              </ThSmall>
              <Th>
                <FormattedMessage id="report.list.header.lastModifiedDate" />
              </Th>
            </TrHead>
          </Thead>
        </Table>
      </div>
      <ScrolledBlock id="canReportScrollTable">
        {!targetGroupId ? (
          <MessagePlaceholder messageId="timeLine.selectGroup" />
        ) : (
          (reports && reports.length > 0 && (
            <Table>
              <Tbody>
                {reports &&
                  reports.map(report => {
                    // 操作権限取得
                    const control = accessible(
                      currentUserRole,
                      currentUserInfo,
                      groups,
                      report,
                    );
                    const clickableRow = control.read || control.edit; // 行クリック可能
                    return (
                      <TrBody
                        disabled={!clickableRow}
                        className={clickableRow && 'clickableItem'}
                        id={`report-${report.id}`}
                        key={`report-${report.id}`}
                        onClick={() => clickableRow && openDetail(report)}
                        checked={checkedItems[report.id]}
                      >
                        {control.delete ? (
                          <TdCheckbox onClick={e => e.stopPropagation()}>
                            <CheckboxContainer>
                              <input
                                type="checkbox"
                                checked={checkedItems[report.id] || false}
                                onChange={e => handleCheckChange(e, report.id)}
                              />
                            </CheckboxContainer>
                          </TdCheckbox>
                        ) : (
                          <TdCheckbox />
                        )}
                        <TdSmall>
                          {createReportNo(
                            report.groupNo,
                            report.reportNo,
                            report.status,
                          )}
                        </TdSmall>
                        <Td>{report.name}</Td>
                        <TdSmall>{getStatusText(report.status)}</TdSmall>
                        <TdSmall>{report.createdOperatorName}</TdSmall>
                        <TdSmall>
                          {convertISOFormatToOnlyDate(report.createdAt)}
                        </TdSmall>
                        <TdSmall>{report.lastUpdatedOperatorName}</TdSmall>
                        <Td>{convertISOFormatToDate(report.updatedAt)}</Td>
                      </TrBody>
                    );
                  })}
              </Tbody>
            </Table>
          )) || <MessagePlaceholder />
        )}
      </ScrolledBlock>
    </MainBlock>
  );
};

ReportList.propTypes = {
  reports: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  currentUserRole: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
};

export default ReportList;
